import axios, { AxiosError } from 'axios'
import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { GetQrStatusResponse, GetTuyaDevicesResponse } from '@/types'

export function useGetQrStatus(keepRefetching: boolean) {
    return useQuery({
        queryKey: ['qrStatus'],
        queryFn: async () => {
            const { data } : { data: GetQrStatusResponse } = await axios.get(
                '/api/user/get_qr_status',
            )
            return data
        },
        refetchInterval: keepRefetching ? 2000 : undefined,
    })
}

export function useSendTuyaDevices() {
    return useQuery({
        queryKey: ['sendTuyaDevices'],
        queryFn: async () => {
            const { data } : { data: any } = await axios.get(
                '/api/user/send_tuya_devices',
            )
            return data
        },
        staleTime: 60*1000*5
    })
}

export function useGetTuyaDevices(trid: number, keepRefetching) {
    return useQuery({
        queryKey: ['getTuyaDevices', trid],
        queryFn: async () => {
            const { data } : { data: GetTuyaDevicesResponse } = await axios.get(
                '/api/user/get_tuya_devices?trid='+trid,
            )
            return data
        },
        refetchInterval: keepRefetching ? 3000 : undefined,
    })
}

// waiting, completed

export const useUpdateUserIdAndGenerateQR = () => {
    const queryClient = useQueryClient()

    const updateTuyaBlobUserId = useMutation({
        mutationFn: (code: string) => {
            return axios.get(`/api/user/update_tuya_blob_user_id?user_id=${code}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['qrStatus'] })
        },
    })

    const generateTuyaQR = useMutation({
        mutationFn: () => {
            return axios.get('/api/user/generate_tuya_qr')
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['qrStatus'] })
            queryClient.invalidateQueries({ queryKey: ['sendTuyaDevices'] })
        },
    })

    const runChainedMutations = async (code: string) => {
        let result
        try {
            await updateTuyaBlobUserId.mutateAsync(code)

            const generateResult = await generateTuyaQR.mutateAsync()
            result = generateResult
        } catch (error) {
            console.error('Došlo k chybě při generování QR kódu:', error)
        }
        return result?.data?.tuya_entry_info_blob?.qr_code ?? null
    }

    return { runChainedMutations }
}

export const useCreateTuyaConnection = (applianceId, setSuccess, setError, setRequestInProgress) => {
    const queryClient = useQueryClient()
    
    return useMutation({
        mutationFn: (deviceId: string) => {            
            return axios.get(
                `/api/appliance_control/create_tuya?device_id=${deviceId}&appliance_id=${applianceId}`
            )
        },
        onSuccess: response => {
            if (response.data?.error) {
                setError(response.data.error)
                setSuccess(null)
            } else {
                queryClient.invalidateQueries({ queryKey: ['applianceControls', applianceId] })
                setSuccess('Úspěšně propojeno')
                setError(null)
            }
        },
        onError: response => {
            if (response instanceof AxiosError && response.response?.data?.error) {
                setError(response.response?.data?.error)
                setSuccess(null)
            } else {
                setError('Nepovedlo se propojit')
                setSuccess(null)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
    })
}

export function useGetConnectedTuyaApp() {
    return useQuery({
        queryKey: ['connectedTuyaApp'],
        queryFn: async () => {
            const { data } : { data: { connected_tuya_app: boolean } } = await axios.get(
                '/api/user/connected_tuya_app',
            )
            return data
        },
    })
}

export const useResetTuyaConnection = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => {
            return axios.get(`/api/user/reset_tuya_connection`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['connectedTuyaApp'] })
        },
    })
}
